import { useState, useRef } from 'react';
import axios from 'axios';
const url = new URL(window.location.href);
const finalUrl = `/api/`;

function JsonTable({
  jsonMode,
  setJsonMode,
  jsonInputRef,
  jsonError,
  setJsonError,
  setJsonFile,
  jsonContent,
  setJsonContent,
  jsonFile,
}) {
  const handleJsonFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        setJsonError('Please upload a .json file');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result;
        validateJson(content); // Always validate the content
        setJsonFile(file); // Set the file regardless of validation
        setJsonContent(content); // Set the content regardless of validation
      };
      reader.readAsText(file);
    }
  };

  const getMode = (arr) => {
    const counts = {};
    arr.forEach((val) => {
      const key = JSON.stringify(val); // Use JSON.stringify to handle different types
      counts[key] = (counts[key] || 0) + 1;
    });
    let maxCount = 0;
    let mode = null;
    for (let key in counts) {
      if (counts[key] > maxCount) {
        maxCount = counts[key];
        mode = JSON.parse(key); // Parse the key back to its original type
      }
    }
    return mode;
  };

  const checkProperties = (jsonArray) => {
    if (!Array.isArray(jsonArray) || jsonArray.length === 0) {
      let arrErr =
        'The input parameter is not a valid JSON array or the array is empty.';
      console.log(arrErr);
      setJsonError(arrErr);
      return;
    }

    const p1Values = jsonArray.map((obj) => obj.Metadata.Source);
    const p2Values = jsonArray.map((obj) => obj.Metadata.ExamBoard);
    const p3Values = jsonArray.map((obj) => obj.Metadata.SyllabusCode);
    const p4Values = jsonArray.map((obj) => obj.Metadata.Subject);
    const p5Values = jsonArray.map((obj) => obj.Metadata.YearOfExam);
    const p6Values = jsonArray.map((obj) => obj.Metadata.Session);
    const p7Values = jsonArray.map((obj) => obj.Metadata.PaperNumber);

    const p1Mode = getMode(p1Values);
    const p2Mode = getMode(p2Values);
    const p3Mode = getMode(p3Values);
    const p4Mode = getMode(p4Values);
    const p5Mode = getMode(p5Values);
    const p6Mode = getMode(p6Values);
    const p7Mode = getMode(p7Values);

    const inconsistentObjects = jsonArray.reduce((result, obj, index) => {
      const Source = obj.Metadata.Source;
      const ExamBoard = obj.Metadata.ExamBoard;
      const SyllabusCode = obj.Metadata.SyllabusCode;
      const Subject = obj.Metadata.Subject;
      const YearOfExam = obj.Metadata.YearOfExam;
      const Session = obj.Metadata.Session;
      const PaperNumber = obj.Metadata.PaperNumber;
      if (
        Source !== p1Mode ||
        ExamBoard !== p2Mode ||
        SyllabusCode !== p3Mode ||
        Subject !== p4Mode ||
        YearOfExam !== p5Mode ||
        Session !== p6Mode ||
        PaperNumber !== p7Mode
      ) {
        result.push({ index, inconsistentProps: [] });
        if (Source !== p1Mode) {
          result[result.length - 1].inconsistentProps.push('Source');
        }
        if (ExamBoard !== p2Mode) {
          result[result.length - 1].inconsistentProps.push('ExamBoard');
        }
        if (SyllabusCode !== p3Mode) {
          result[result.length - 1].inconsistentProps.push('SyllabusCode');
        }
        if (Subject !== p4Mode) {
          result[result.length - 1].inconsistentProps.push('Subject');
        }
        if (YearOfExam !== p5Mode) {
          result[result.length - 1].inconsistentProps.push('YearOfExam');
        }
        if (Session !== p6Mode) {
          result[result.length - 1].inconsistentProps.push('Session');
        }
        if (PaperNumber !== p7Mode) {
          result[result.length - 1].inconsistentProps.push('PaperNumber');
        }
      }
      return result;
    }, []);

    const summary = inconsistentObjects
      .map(
        (item) =>
          `Object at index ${item.index + 1} is inconsistent, inconsistent properties: ${item.inconsistentProps.join(', ')}. \n`,
      )
      .join('\n');
    if (inconsistentObjects.length > 0) {
      console.log(summary);
      setJsonError(summary);
    }
    return;
  };

  const validateJson = (content) => {
    try {
      let jsonArray = JSON.parse(content);
      setJsonError('');
      checkProperties(jsonArray);
      return true;
    } catch (e) {
      const errorMsg = e.message;
      const match = errorMsg.match(/position\s+(\d+)/);
      const position = match ? parseInt(match[1]) : null;
      // Extract error type
      let errorType = '';
      if (errorMsg.includes('Unexpected token')) {
        errorType = 'Unexpected token found';
      } else if (errorMsg.includes('Expected')) {
        errorType = 'Missing required symbol';
      } else if (errorMsg.includes('Unterminated string')) {
        errorType = 'Unterminated string';
      } else if (errorMsg.includes('Unexpected end of JSON')) {
        errorType = 'Incomplete JSON structure';
      } else if (errorMsg.includes('Duplicate key')) {
        errorType = 'Duplicate key found';
      }
      // Calculate line number and provide detailed hint
      if (position !== null) {
        const lines = content.slice(0, position).split('\n');
        const lineNumber = lines.length;
        setJsonError(
          `Error at line ${lineNumber}: ${errorType || 'Syntax error'}`,
        );
      } else {
        setJsonError(`JSON Format Error: ${errorType || 'Syntax error'}`);
      }
      return false;
    }
  };

  const handleJsonTextChange = (e) => {
    const content = e.target.value;
    setJsonContent(content);
    if (content) {
      validateJson(content);
    } else {
      setJsonError('');
    }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-700">
          JSON Upload Area
        </h2>
        {/* <div className="flex gap-4">
          <button
            className={`px-4 py-2 !rounded-button whitespace-nowrap ${
              jsonMode === 'file'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
            onClick={() => setJsonMode('file')}
          >
            File Upload
          </button>
          <button
            className={`px-4 py-2 !rounded-button whitespace-nowrap ${
              jsonMode === 'text'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
            onClick={() => setJsonMode('text')}
          >
            Text Input
          </button>
        </div> */}
        {/* <div className="flex border-b border-gray-200"> */}
        <div className="flex">
          {/* <button
            className={`bg-white px-4 py-2 -mb-px whitespace-nowrap ${
              jsonMode === 'file'
                ? 'border-b-2 border-blue-500 text-blue-600 font-medium'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setJsonMode('file')}
          > */}
          <button
            style={
              jsonMode === 'file'
                ? {
                    borderBottom: '2px solid #0056b3',
                    color: '#0056b3',
                    borderRadius: '0',
                  }
                : { borderBottom: 'none', color: 'gray', borderRadius: '0' }
            }
            className="bg-white px-4 py-2 -mb-px whitespace-nowrap"
            onClick={() => {
              setJsonMode('file');
            }}
          >
            File Upload
          </button>
          {/* <button
            className={`bg-white px-4 py-2 -mb-px whitespace-nowrap ${
              jsonMode === 'text'
                ? 'border-b-2 border-blue-500 text-blue-600 font-medium'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setJsonMode('text')}
          > */}
          <button
            style={
              jsonMode === 'text'
                ? {
                    borderBottom: '2px solid #0056b3',
                    color: '#0056b3',
                    borderRadius: '0',
                  }
                : { borderBottom: 'none', color: 'gray', borderRadius: '0' }
            }
            className="bg-white px-4 py-2 -mb-px whitespace-nowrap"
            onClick={() => {
              setJsonMode('text');
            }}
          >
            Text Input
          </button>
        </div>
      </div>
      {jsonMode === 'file' ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            ref={jsonInputRef}
            type="file"
            accept=".json"
            onChange={handleJsonFileChange}
            className="hidden"
          />
          <button
            onClick={() => jsonInputRef.current?.click()}
            className="mb-4 px-6 py-3 bg-blue-600 text-white !rounded-button whitespace-nowrap hover:bg-blue-700"
          >
            Choose JSON File
          </button>
          <p className="text-gray-500">Or drag and drop file here</p>
          {jsonFile && (
            <>
              <p className="mt-4 text-green-600">
                Selected file: {jsonFile.name}
              </p>
              <div className="mt-4 w-full bg-gray-50 p-4 rounded-lg text-left">
                <p
                  className={`text-sm ${
                    jsonError ? 'text-red-600' : 'text-green-600'
                  } mb-2`}
                >
                  Parse result:
                </p>
                <pre className="w-full text-sm bg-white p-4 rounded border border-gray-200 overflow-auto max-h-[400px] relative">
                  {(() => {
                    try {
                      return JSON.stringify(JSON.parse(jsonContent), null, 2);
                    } catch {
                      return jsonContent;
                    }
                  })()
                    .split('\n')
                    .map((line, i) => {
                      const hasError =
                        line.includes('error') || line.includes('invalid');
                      return (
                        <div key={i} className="flex">
                          <span className="select-none text-gray-400 w-8 text-right pr-2 mr-2 border-r border-gray-200">
                            {i + 1}
                          </span>
                          <span className={hasError ? 'text-red-500' : ''}>
                            {line}
                          </span>
                        </div>
                      );
                    })}
                </pre>
              </div>
            </>
          )}
        </div>
      ) : (
        <div>
          <div className="relative">
            <textarea
              className="w-full h-48 p-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 leading-6 overflow-y-auto resize-none"
              placeholder="Please enter JSON formatted text content..."
              value={jsonContent}
              onChange={handleJsonTextChange}
            />
          </div>
          {jsonContent && (
            <div className="mt-4 w-full bg-gray-50 p-4 rounded-lg">
              <p
                className={`text-sm ${
                  jsonError ? 'text-red-600' : 'text-green-600'
                } mb-2`}
              >
                Parse result:
              </p>
              <pre className="w-full text-sm bg-white p-4 rounded border border-gray-200 overflow-auto max-h-[400px] relative">
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(jsonContent), null, 2);
                  } catch {
                    return jsonContent;
                  }
                })()
                  .split('\n')
                  .map((line, i) => {
                    const hasError =
                      line.includes('error') || line.includes('invalid');
                    return (
                      <div key={i} className="flex">
                        <span className="select-none text-gray-400 w-8 text-right pr-2 mr-2 border-r border-gray-200">
                          {i + 1}
                        </span>
                        <span className={hasError ? 'text-red-500' : ''}>
                          {line}
                        </span>
                      </div>
                    );
                  })}
              </pre>
            </div>
          )}
        </div>
      )}
      {jsonError && (
        <div className="mt-2">
          <div className="flex items-center p-4 bg-red-50 rounded border border-red-200">
            <i className="fas fa-exclamation-circle text-red-500 mr-2"></i>
            <span className="text-red-500">{jsonError}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ImageTable({ imageFiles, setImageFiles, jsonContent }) {
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => file.type.startsWith('image/'));
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageFiles((prev) => [
          ...prev,
          {
            file,
            preview: e.target?.result,
            jsonIndex: '',
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const imageInputRef = useRef(null);

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-700 mb-4">
        Image Upload Area
      </h2>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageChange}
          className="hidden"
        />
        <div className="text-center mb-6">
          <button
            onClick={() => imageInputRef.current?.click()}
            className="px-6 py-3 bg-blue-600 text-white !rounded-button whitespace-nowrap hover:bg-blue-700"
          >
            Choose Images
          </button>
          <p className="mt-2 text-gray-500">Supports all image formats</p>
        </div>
        {imageFiles.length > 0 && (
          <div>
            <div className="grid grid-cols-4 gap-4">
              {imageFiles.map((file, index) => (
                <div key={index} className="relative group">
                  <img
                    src={file.preview}
                    alt={file.file.name}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity rounded-b-lg">
                    {file.file.name}
                  </div>
                  <select
                    className="absolute top-2 left-2 bg-white bg-opacity-90 border border-gray-300 rounded px-2 pr-8 py-1 text-sm"
                    value={file.jsonIndex}
                    onChange={(e) => {
                      try {
                        const jsonArr = JSON.parse(jsonContent);
                        if (Array.isArray(jsonArr)) {
                          const nJsonIndex = e.target.value;
                          console.log(
                            `Image ${
                              index + 1
                            } is mapped to JSON index: ${nJsonIndex}`,
                          );
                          setImageFiles((prev) =>
                            prev.map((f, i) =>
                              i === index ? { ...f, jsonIndex: nJsonIndex } : f,
                            ),
                          );
                        }
                      } catch (error) {
                        console.error('Invalid JSON');
                      }
                    }}
                  >
                    <option value="">Select JSON index</option>
                    {(() => {
                      try {
                        const jsonArr = JSON.parse(jsonContent);
                        if (Array.isArray(jsonArr)) {
                          return jsonArr.map((_, idx) => (
                            <option key={idx} value={idx}>
                              Index {idx + 1}
                            </option>
                          ));
                        }
                        return null;
                      } catch (error) {
                        return null;
                      }
                    })()}
                  </select>
                </div>
              ))}
            </div>
            <div className="mt-4 text-sm text-gray-600">
              <i className="fas fa-info-circle mr-2"></i>
              Select a JSON index from the dropdown menu above each image to map
              it to the corresponding JSON object.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UploadTable() {
  const [isUploading, setIsUploading] = useState(false);
  const [jsonError, setJsonError] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [jsonContent, setJsonContent] = useState('');
  const [jsonMode, setJsonMode] = useState('file');
  const jsonInputRef = useRef(null);
  const [jsonFile, setJsonFile] = useState(null);
  const isSubmitDisabled = !!jsonError || isUploading;

  const handleSubmit = async () => {
    if (jsonError) return;
    setIsUploading(true);
    const formData = new FormData();
    for (const fileObject of imageFiles) {
      formData.append('files', fileObject.file);
      formData.append('index', fileObject.jsonIndex);
    }

    formData.append('jsonData', jsonContent);

    try {
      for (let pair of formData.entries()) {
        console.log(pair[0] + ', ' + pair[1]);
      }
      const response = await axios.post(
        '/api/syncQuestionBank',
        formData,
        {},
      );
      console.log(response.status);
      if (response.status === 200) {
        alert('Upload successful!');
      } else {
        alert('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-2xl font-bold mb-8 text-gray-800">File Upload</h1>
          <JsonTable
            jsonMode={jsonMode}
            setJsonMode={setJsonMode}
            jsonInputRef={jsonInputRef}
            jsonError={jsonError}
            setJsonError={setJsonError}
            setJsonFile={setJsonFile}
            jsonContent={jsonContent}
            setJsonContent={setJsonContent}
            jsonFile={jsonFile}
          />
          <ImageTable
            imageFiles={imageFiles}
            setImageFiles={setImageFiles}
            jsonContent={jsonContent}
          />
          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              className={`px-8 py-3 !rounded-button whitespace-nowrap ${
                isSubmitDisabled
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white flex items-center`}
            >
              {isUploading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Uploading...
                </>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Upload() {
  return (
    <>
      <UploadTable />
    </>
  );
}
